// src/features/audit/AuditPage.tsx
import { useEffect, useState } from 'react';
import { Card, EmptyState } from '../../app/components/foundation';
import { ControlPlaneDataSurface, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { AuditHeader } from './components';
import { getAuditEvents } from '../security/api/security.api';
import type { AuditEvent } from '../security/types/security.types';
import './styles/audit.css';

/**
 * Audit Foundation. Same layout pattern as CustomersPage/
 * SecurityPage: a PageContainer shell, a feature-owned header, then
 * feature content below — RTL and Persian throughout, using only
 * existing tokens/primitives (Card, PageHeader) via
 * app/components/foundation.
 *
 * Every row here comes from features/audit/data/audit.mock.ts via
 * useAuditMock() — no audit endpoint exists yet. useAuditMock is the
 * seam a later phase swaps for a real API hook (same shape,
 * `{ audits }`); AuditTable only depends on the `audits` prop, not on
 * where it came from, so that swap won't touch this file or
 * AuditTable.tsx.
 */
export function AuditPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = loading && events.length === 0;

  useEffect(() => {
    getAuditEvents()
      .then(setEvents)
      .catch(() => setError('خطا در دریافت رخدادهای امنیتی'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <ControlPlanePage className="vania-audit-control-plane-page" title="">
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          <AuditHeader />
          {error && !loading ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          {initialLoading ? <AuditTableSkeleton /> : null}
          {!loading && !error && events.length === 0 ? <EmptyState message="رخداد امنیتی برای نمایش وجود ندارد." /> : null}
          {events.length > 0 ? <div className="vania-audit-table-card"><div className="vania-audit-events-table-region"><div className="vania-audit-events-table-scroll"><table className="vania-audit-events-table"><thead><tr><th>شناسه کاربر</th><th>نوع رخداد</th><th>منبع</th><th>زمان</th></tr></thead><tbody>{events.map((event) => <tr key={event.id}><td dir="ltr">{event.telegram_id}</td><td>{event.action_type}</td><td>{event.event_source}</td><td dir="ltr">{event.created_at}</td></tr>)}</tbody></table></div></div></div> : null}
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
    </ControlPlanePage>
  );
}

function AuditTableSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری رخدادهای امنیتی">
      <Card noPadding className="vania-audit-table-card" aria-hidden="true">
        <div className="vania-audit-events-table-region">
          <div className="vania-audit-events-table-scroll">
          <table className="vania-audit-events-table">
            <thead>
              <tr>
                {['شناسه کاربر', 'نوع رخداد', 'منبع', 'زمان'].map((heading) => <th key={heading}>{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }, (_, rowIndex) => (
                <tr key={`audit-skeleton-${rowIndex}`}>
                  {Array.from({ length: 4 }, (_, cellIndex) => (
                    <td key={`audit-skeleton-${rowIndex}-${cellIndex}`}><VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </Card>
    </VaniaLoadingSystem>
  );
}
